import { ApiError, PlanLimitError } from '@/lib/api-error'
import { dispatchPlanLimitEvent } from '@/components/shared/PlanLimitModal'

function getBaseUrl(): string {
  const proxyUrl = sessionStorage.getItem('proxy_url')
  return `${proxyUrl ?? 'http://localhost:3456'}/api`
}

let _onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(fn: () => void) {
  _onUnauthorized = fn
}

// Without this, a request stuck behind a stale/dead connection (e.g. the
// proxy's DB link surviving a wifi/ethernet switch) hangs forever — fetch()
// has no default timeout — and any UI awaiting it (a loading spinner) never
// resolves. Bound it so callers always get a rejection to react to.
const FETCH_TIMEOUT_MS = 20_000

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('veridia_token')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const timeoutController = new AbortController()
  const timeoutId = setTimeout(() => timeoutController.abort(), FETCH_TIMEOUT_MS)

  let res: Response
  try {
    res = await fetch(`${getBaseUrl()}${path}`, { ...options, headers, signal: options.signal ?? timeoutController.signal })
  } catch {
    throw new ApiError(0, 'Sin conexión')
  } finally {
    clearTimeout(timeoutId)
  }

  if (res.status === 401) {
    _onUnauthorized?.()
    throw new ApiError(401, 'Sesión expirada')
  }

  if (res.status === 402) {
    let body: { error?: string; limit?: number; current?: number } = {}
    try { body = await res.json() as typeof body } catch { /* ignore */ }
    const err = new PlanLimitError(body.error ?? 'Límite del plan alcanzado', body.limit ?? 0, body.current ?? 0)
    dispatchPlanLimitEvent({ message: err.message, limit: err.limit, current: err.current })
    throw err
  }

  if (!res.ok) {
    let message = 'Error del servidor'
    try {
      const body = await res.json() as { error?: string }
      if (body.error) message = body.error
    } catch { /* ignore */ }
    throw new ApiError(res.status, message)
  }

  return res.json() as Promise<T>
}
